import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { useBackoffice } from '../backoffice/useBackoffice';

export enum Tokens {
  USDC = 'usdc',
  USDT = 'usdt'
}

export enum Networks {
  POLYGON = 'polygon',
  ETHEREUM = 'ethereum',
  TRON = 'tron',
  ARBITRUM = 'arbitrum',
  BASE = 'base',
  SOLANA = 'solana'
}

export enum Currencies {
  USD = 'usd',
  EUR = 'eur',
  AED = 'aed'
}

export enum PaymentRails {
  ACH = 'ach',
  WIRE = 'wire',
  INTERNATIONAL_WIRE = 'international_wire',
  UAEFTS = 'uaefts',
  SEPA = 'sepa'
}

type TransferData = {
  onramp_transfer?: {
    id: string;
    status: 'pending' | 'processing' | 'completed';
    amount: string;
    source_currency: string;
    source_rails: string;
    destination_network: string;
    destination_token: string;
    destination_address: string;
    quote: {
      deposit_rails: string;
      deposit_currency: string;
      deposit_bank_account: {
        bank_name: string;
        bank_address: string;
        account_beneficiary_type: string;
        account_beneficiary_name: string;
        // account_beneficiary_address: string;
        account_type: string;
        iban?: {
          bic: string;
          iban_number: string;
        };
        us?: {
          account_number: string;
          routing_number: string;
        };
      };
      deposit_amount: string;
      deposit_message: string;
      fee_amount: string;
    };
  }
  offramp_transfer?: {
    id: string;
    status: 'pending' | 'processing' | 'completed';
    amount: string;
    source_token: string;
    source_network: string;
    destination_currency: string;
    destination_payment_rails: string;
    transfer_purpose: string;
    destination_bank_account: {
      bank_name: string;
      account_holder_type: 'individual' | 'business';
      account_holder_first_name?: string;
      account_holder_last_name?: string;
      account_holder_business_name?: string;
      account_holder_address: {
        country: string;
        city: string;
        street_line_1: string;
        postal_code: string;
      };
      account_type: 'iban' | 'us';
      iban?: {
        bic?: string;
        iban_number?: string;
      };
      us?: {
        account_number?: string;
        routing_number?: string;
      };
      international_wire?: {
        account_number: string;
        routing_number?: string;
        bic: string;
      };
    };
    quote: {
      deposit_network: string;
      deposit_token: string;
      deposit_blockchain_address: string;
      deposit_amount: string;
      source_amount: string;
      destination_amount: string;
      exchange_rate: string;
      fee_amount: string;
      deposit_transaction_hash?: string | null;
    };
  };
};

export const useTransfer = (transferId?: string, type?: string) => {
  const { getAccessTokenSilently } = useAuth0();
  const { developerId } = useBackoffice();

  const customersQuery = useQuery({
    queryKey: ['customers', 'transfers', transferId],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const res = await axios.get<{ data: TransferData }>(
        `${process.env.REACT_APP_BACKEND_API_URL}/customers/transfers/${transferId}`,
        {
          params: {
            type,
            consumerId: developerId,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return res.data?.data;
    },
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 2,
    enabled: !!transferId,
  });

  return customersQuery;
};


export const useCreateTransfer = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { developerId } = useBackoffice();
  const mutation = useMutation({
    mutationKey: ['create-transfer'],
    mutationFn: async (data: {
      payload: {
        customer_id: string
        quote_id: string
        transfer_type: 'offramp' | 'onramp'
        offramp?: {
          destination_external_account_id: string;
        }
      }
    }) => {
      const token = await getAccessTokenSilently();
      const res = await axios.post<{ data: TransferData }>(
        `${process.env.REACT_APP_BACKEND_API_URL}/customers/transfers`, data.payload,
        {
          params: {
            consumerId: developerId,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return res.data?.data;
    },
  })

  return mutation

}